/* ============================================================
   sk-testimonials.js — homepage testimonials from LIVE Judge.me data.

   sk-testimonials.liquid prints shop.metafields.judgeme.featured_carousel
   (Judge.me's pre-rendered Reviews Carousel HTML, which Judge.me itself
   refreshes on every new review) inside an inert <template>. We read its
   .jdgm-carousel-item nodes and rebuild them as our own .sk-tm__card
   markup — textContent only, never innerHTML — then swap out the
   hand-typed fallback blocks. If nothing parses, the fallback stays.

   Which reviews Judge.me puts in the carousel (recent 5-star, or the ones
   marked Featured) is a Judge.me admin setting. Whatever it is set to, only
   5-star reviews are shown here (owner rule); a review whose rating can't
   be read is skipped rather than assumed to be 5 stars.
   ============================================================ */
(function () {
  'use strict';

  const section = document.querySelector('[data-sk-tm-live]');
  const tpl = section && section.querySelector('[data-sk-tm-src]');
  if (!tpl || !('content' in tpl)) return; // no live data, or a browser without <template>: fallback stays
  const max = parseInt(section.getAttribute('data-sk-tm-max'), 10) || 12;

  /** @param {Element} el @param {string} sel @returns {string} */
  function text(el, sel) {
    const n = el.querySelector(sel);
    return n ? n.textContent.replace(/\s+/g, ' ').trim() : '';
  }

  /** @param {string} name @returns {string} */
  function initials(name) {
    const parts = name.trim().split(/\s+/);
    const a = parts[0] ? parts[0].charAt(0) : '';
    const b = parts.length > 1 ? parts[parts.length - 1].charAt(0) : '';
    return (a + b).toUpperCase();
  }

  /** @param {Element} el @returns {number} 1-5, or 0 when the rating can't be read */
  function score(el) {
    const r = el.querySelector('.jdgm-carousel-item__review-rating');
    const m = r && (r.getAttribute('aria-label') || '').match(/(\d)/);
    const on = r ? r.querySelectorAll('.jdgm-star.jdgm--on').length : 0;
    const s = m ? parseInt(m[1], 10) : on;
    return Math.max(0, Math.min(5, s));
  }

  /** @typedef {{score:number, body:string, name:string, role:string, href:string}} Review */

  /** @param {Element} el @returns {Review|null} */
  function parse(el) {
    const stars = score(el);
    if (stars < 5) return null; // 5-star only, even if the Judge.me carousel setting changes
    let body = text(el, '.jdgm-carousel-item__review-body');
    if (body.length < 12) body = text(el, '.jdgm-carousel-item__review-title');
    if (body.length < 12) return null; // skip empty / one-word reviews
    const link = el.querySelector('.jdgm-carousel-item__product a[href]');
    let href = link ? link.getAttribute('href') : '';
    if (href && !/^\/products\//.test(href)) href = ''; // same-origin product links only
    const isShop = el.classList.contains('jdgm--shop-review');
    // Judge.me's carousel names the product only via the thumbnail's alt text.
    const img = el.querySelector('.jdgm-carousel-item__product-image');
    const product = text(el, '.jdgm-carousel-item__product-title') || (img && img.getAttribute('alt')) || '';
    return {
      score: stars,
      body: body,
      name: text(el, '.jdgm-carousel-item__reviewer-name') || 'Verified customer',
      role: isShop ? 'Store review' : product.trim(),
      href: href
    };
  }

  /** Mirrors the Liquid fallback card markup 1:1 so the existing CSS applies unchanged.
   *  @param {Review} r @param {boolean} dup @returns {HTMLElement} */
  function card(r, dup) {
    const el = document.createElement(r.href ? 'a' : 'article');
    el.className = 'sk-tm__card' + (dup ? ' sk-tm__card--dup' : '');
    if (r.href) el.setAttribute('href', r.href);
    if (dup) {
      el.setAttribute('aria-hidden', 'true');
      if (r.href) el.setAttribute('tabindex', '-1');
    }

    const st = document.createElement('span');
    st.className = 'st';
    st.setAttribute('aria-label', r.score + ' out of 5 stars');
    st.textContent = '★'.repeat(r.score) + '☆'.repeat(5 - r.score);
    el.appendChild(st);

    const rq = document.createElement('p');
    rq.className = 'rq';
    rq.textContent = r.body; // review text is user content: textContent only
    el.appendChild(rq);

    const rby = document.createElement('div');
    rby.className = 'rby';
    const av = document.createElement('span');
    av.className = 'av';
    av.setAttribute('aria-hidden', 'true');
    av.textContent = initials(r.name);
    rby.appendChild(av);
    const meta = document.createElement('div');
    const rn = document.createElement('div');
    rn.className = 'rn';
    rn.textContent = r.name;
    meta.appendChild(rn);
    if (r.role) {
      const rr = document.createElement('div');
      rr.className = 'rr';
      rr.textContent = r.role;
      meta.appendChild(rr);
    }
    rby.appendChild(meta);
    el.appendChild(rby);
    return el;
  }

  const items = tpl.content.querySelectorAll('.jdgm-carousel-item');
  const reviews = [];
  for (const item of items) {
    const r = parse(item);
    if (r) reviews.push(r);
    if (reviews.length >= max) break;
  }
  if (!reviews.length) return; // fallback blocks stay

  const rail = section.querySelector('[data-sk-tm-live-rail]');
  const track = section.querySelector('[data-sk-tm-live-track]');
  if (!rail || !track) return;
  const frag = document.createDocumentFragment();
  for (const dup of [false, true]) {
    for (const r of reviews) frag.appendChild(card(r, dup));
  }
  track.appendChild(frag);
  rail.hidden = false;
  const fallback = section.querySelector('[data-sk-tm-fallback]');
  if (fallback) fallback.remove();
  tpl.remove();
})();
